/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import { FieldArray } from 'react-final-form-arrays'
import { validateRequired } from 'utils/validation'
import AnswersFieldArray, { MIN_ANSWERS_COUNT } from '../AnswersFieldArray'
import { FormState } from '../consts'
import * as formClasses from '../styles'
import InvertImagesField from '../InvertImagesField'
import { MultipleSelectionLabelCheckBox } from '../MultipleSelectionLabelCheckBox'
import { getQuestionTypeConfig } from '../questionTypeConfig'

const validateAnswers = (value: FormState['answer'], allValues: FormState) => {
  const config = getQuestionTypeConfig((allValues as FormState).type)

  if ((value?.length ?? 0) < MIN_ANSWERS_COUNT) {
    return `Should be at least ${MIN_ANSWERS_COUNT} options`
  }

  const correctCount = value?.filter(({ isCorrect }) => isCorrect).length ?? 0

  if (config.requiredCorrectCount) {
    if (correctCount !== config.requiredCorrectCount) {
      return `Exactly ${config.requiredCorrectCount} options must be the correct answers`
    }
  } else if (config.answerMode === 'single' && correctCount !== 1) {
    return 'Only one option must be the correct answer'
  } else if (correctCount < 1) {
    return 'At least one option must be the correct answer'
  }

  if (config.hasPoints) {
    const toPoints = (point?: number | null) => Math.round((point ?? 0) * 100) / 100

    const hasInvalidWrongPoints = value
      ?.filter(({ isCorrect }) => !isCorrect)
      .some(({ point }) => toPoints(point) !== 1)

    if (hasInvalidWrongPoints) {
      return 'Each wrong option must have -1 points'
    }

    // use * 100 / 100 to avoid floating-point rounding errors when summing decimals (e.g., 0.6 + 0.3 -> 0.89999 instead of 0.9)
    const pointsSum =
      value
        ?.filter(({ isCorrect }) => isCorrect)
        .map(({ point }) => Math.round((point ?? 0) * 100))
        .reduce((acc, point) => acc + point, 0) / 100

    if (pointsSum !== 1) {
      return 'Sum of points of the correct answers should be 1'
    }
  }

  return undefined
}

type Props = {
  values: FormState
  onAnswerChange: () => void
}

export const OptionChoices: FC<Props> = ({ onAnswerChange, values }) => {
  const config = getQuestionTypeConfig(values.type)

  return (
    <>
      <div css={formClasses.title}>Option Choices</div>
      <MultipleSelectionLabelCheckBox />
      <FieldArray name="answer" validate={(value, allValues) => validateAnswers(value, allValues as FormState)}>
        {({ fields, meta }) => (
          <AnswersFieldArray
            fields={fields}
            meta={meta}
            onAnswerChange={onAnswerChange}
            isMultiple={config.answerMode === 'multiple'}
            showPoints={config.hasPoints}
            optionInput={config.optionInput}
          />
        )}
      </FieldArray>
      {config.optionInput === 'html' && (
        <InvertImagesField
          name="invertAnswerImages"
          title="Option Choices"
          validate={validateRequired}
          htmlWithImagesForPreview={values.answer?.map(({ textAsHtml }) => textAsHtml ?? '').join('')}
        />
      )}
    </>
  )
}
